import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Stack,
  Card,
  CardContent,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ExpandMore,
  Search,
  WhatsApp,
  FlightTakeoff,
  ConfirmationNumber,
  ContactSupport,
  CheckCircle,
  HelpOutline,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { HeaderV2, FooterV2 } from '../../components/layout';
import { colors } from '../../components/theme/colors';

const FAQS = [
  {
    category: 'Umrah & Visas',
    question: 'How do I book an Umrah package with Rehman Travels?',
    answer: 'You can explore our curated Umrah packages on the Trips/Umrah page, choose between 10-day, 12-day, or 14-day packages, or use our "Customize" page to build your own itinerary with custom hotels and transport. Alternatively, contact our 24/7 WhatsApp desk for custom family packages.'
  },
  {
    category: 'Umrah & Visas',
    question: 'Do your packages include Umrah visa processing?',
    answer: 'Yes, all our full Umrah packages include electronic Umrah visa processing. Required documents include a clear passport scan (with at least 6 months validity) and passport-sized photographs with a white background.'
  },
  {
    category: 'Flight Quotations',
    question: 'How does the Flight Fare Request work?',
    answer: 'When you search flights on our website, you can submit a quote request. Our reservation agents immediately look up wholesale consolidator inventory and send you the lowest confirmed price via WhatsApp and Email, usually within 15–30 minutes.'
  },
  {
    category: 'Flight Quotations',
    question: 'How can I track my flight request status?',
    answer: 'You can track your quotation anytime at "/v2/flights/track" by entering your Request ID and 4-digit Tracking PIN, or by logging into your Customer Dashboard with your phone number and PIN.'
  },
  {
    category: 'Payments & Bookings',
    question: 'What payment methods do you accept?',
    answer: 'We accept Bank Transfers (Meezan, HBL, Bank Alfalah, Allied Bank), Credit/Debit Cards, PayPak, and cash payment at our Blue Area Islamabad and Gulberg Lahore offices.'
  },
  {
    category: 'Cancellations & Refunds',
    question: 'What is your package cancellation policy?',
    answer: 'Cancellations requested 30+ days prior to travel receive up to a 90% refund. Cancellations made 15–29 days prior receive a 50% refund. Cancellations under 15 days are subject to hotel and airline non-refundable terms.'
  },
];

const HelpCenterPageV2 = () => {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Umrah & Visas', 'Flight Quotations', 'Payments & Bookings', 'Cancellations & Refunds'];

  const filteredFaqs = FAQS.filter((faq) => {
    const matchesCat = selectedCategory === 'All' || faq.category === selectedCategory;
    const matchesSearch =
      !search ||
      faq.question.toLowerCase().includes(search.toLowerCase()) ||
      faq.answer.toLowerCase().includes(search.toLowerCase());
    return matchesCat && matchesSearch;
  });

  return (
    <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <HeaderV2 />

      {/* Hero Search */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0b2239 0%, #1e4d79 100%)',
          color: '#fff',
          pt: { xs: 12, md: 14 },
          pb: { xs: 6, md: 8 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ color: 'secondary.main', fontWeight: 700, letterSpacing: 1.5 }}>
            REHMAN TRAVELS HELP CENTRE & SERVICES
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, mt: 1, mb: 2, fontFamily: "'Fraunces', Georgia, serif" }}>
            How can we help you today?
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.88, maxWidth: 600, mx: 'auto', mb: 4 }}>
            Find quick answers for visas, bookings, flight quotes, and 24/7 travel assistance.
          </Typography>

          <TextField
            fullWidth
            placeholder="Search questions, visa rules, flight quotes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              maxWidth: 600,
              mx: 'auto',
              backgroundColor: '#fff',
              borderRadius: 3,
              '& .MuiOutlinedInput-root': { borderRadius: 3 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Container>
      </Box>

      {/* Quick Action Tiles */}
      <Container maxWidth="lg" sx={{ py: 4, mt: -3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={2}
              sx={{
                borderRadius: 3,
                textAlign: 'center',
                p: 2,
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)' },
              }}
            >
              <CardContent>
                <FlightTakeoff sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Track Flight Quote
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Check status of your fare quotation with PIN.
                </Typography>
                <Button component={RouterLink} to="/v2/flights/track" variant="outlined" size="small" fullWidth>
                  Track Quote
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={2}
              sx={{
                borderRadius: 3,
                textAlign: 'center',
                p: 2,
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)' },
              }}
            >
              <CardContent>
                <ConfirmationNumber sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Customer Dashboard
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Sign in with phone & PIN to see all bookings.
                </Typography>
                <Button component={RouterLink} to="/v2/dashboard" variant="outlined" size="small" fullWidth>
                  Open Dashboard
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={2}
              sx={{
                borderRadius: 3,
                textAlign: 'center',
                p: 2,
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)' },
              }}
            >
              <CardContent>
                <WhatsApp sx={{ fontSize: 40, color: '#25D366', mb: 1 }} />
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  WhatsApp Support
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Instant live chat with our travel agents.
                </Typography>
                <Button
                  component="a"
                  href="https://wa.me/923000778844"
                  target="_blank"
                  rel="noopener"
                  variant="contained"
                  size="small"
                  fullWidth
                  sx={{ backgroundColor: '#25D366', '&:hover': { backgroundColor: '#128C7E' } }}
                >
                  Chat Now
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={2}
              sx={{
                borderRadius: 3,
                textAlign: 'center',
                p: 2,
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)' },
              }}
            >
              <CardContent>
                <ContactSupport sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Contact Desk
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Phone helpline & branch office locations.
                </Typography>
                <Button component={RouterLink} to="/v2/contact" variant="outlined" size="small" fullWidth>
                  Contact Us
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Frequently Asked Questions */}
      <Container maxWidth="md" sx={{ py: 4, mb: 4, flexGrow: 1 }}>
        <Typography variant="h4" fontWeight={800} sx={{ mb: 3, textAlign: 'center', fontFamily: "'Fraunces', Georgia, serif" }}>
          Frequently Asked Questions
        </Typography>

        {/* Category Pills */}
        <Stack direction="row" spacing={1} sx={{ mb: 3, overflowX: 'auto', pb: 1, justifyContent: { sm: 'center' } }}>
          {categories.map((cat) => (
            <Button
              key={cat}
              variant={selectedCategory === cat ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setSelectedCategory(cat)}
              sx={{ borderRadius: 5, whiteSpace: 'nowrap' }}
            >
              {cat}
            </Button>
          ))}
        </Stack>

        <Stack spacing={1.5}>
          {filteredFaqs.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
              <HelpOutline sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="subtitle1">No matching questions found.</Typography>
              <Typography variant="body2" color="text.secondary">
                Try searching for another keyword or reach out on WhatsApp.
              </Typography>
            </Paper>
          ) : (
            filteredFaqs.map((faq, index) => (
              <Accordion
                key={index}
                elevation={0}
                sx={{
                  border: `1px solid ${colors.border.light}`,
                  borderRadius: '12px !important',
                  '&:before': { display: 'none' },
                  mb: 1,
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight={600} sx={{ fontSize: '1.02rem' }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Stack>
      </Container>

      <FooterV2 />
    </Box>
  );
};

export default HelpCenterPageV2;
